import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { AUDITED_READ } from '@/features/humint/mappers/subject-page'
import { humintSubjectPageApi } from '@/services/api/humint-subject-page'
import { identityAudioApi } from '@/services/api/identity-audio'

export function useIdentityAudio(caseId: string, subjectRef: string) {
  const client = useQueryClient()
  const [storedId, setStoredId] = useState<string | null>(null)
  const key = ['case', caseId, 'identity-audio', subjectRef]
  const query = useQuery({
    queryKey: key,
    queryFn: ({ signal }) => identityAudioApi.read(caseId, subjectRef, signal),
    ...AUDITED_READ,
  })
  const pending = (query.data?.items ?? [])
    .filter(row => row.kind === 'voice_sample' && row.state === 'processing')
    .map(row => row.capture_id)
  if (storedId) pending.push(storedId)
  const states = useQuery({
    queryKey: [...key, 'states'],
    queryFn: ({ signal }) => humintSubjectPageApi.captureStates(caseId, subjectRef, signal),
    enabled: Boolean(query.data) && pending.length > 0,
    refetchInterval: poll =>
      pending.some(
        id =>
          id && !poll.state.data?.some(row => row.capture_id === id && row.state !== 'processing')
      )
        ? 4000
        : false,
    refetchOnWindowFocus: false,
    retry: false,
    gcTime: 0,
  })
  const items = (query.data?.items ?? []).map(row => {
    const state = states.data?.find(state => state.capture_id === row.capture_id)
    return state
      ? { ...row, state: state.state, voice_quality: state.voice_quality ?? row.voice_quality }
      : row
  })
  const onStored = (id: string) => {
    setStoredId(id)
    client.setQueryData<Awaited<ReturnType<typeof humintSubjectPageApi.captureStates>>>(
      [...key, 'states'],
      rows => rows?.filter(row => row.capture_id !== id)
    )
    void client.invalidateQueries({ queryKey: key })
  }
  return { query, items, onStored, states, retry: () => void query.refetch() }
}
